<template>
    <div class="CollateralMappingTable min-w-[800px]">
        <RbacDisableGuard v-slot="{ isDisabledByRbac }" :features="[RbacFeature.entityDetailsEditProperties]">
            <Table>
                <template #head>
                    <HeadCell>Required Collateral</HeadCell>
                    <HeadCell>Provided Collateral</HeadCell>
                    <HeadCell />
                    <HeadCell />
                </template>
                <CollateralMappingTableRow
                    v-for="(item, i) in collateralAndOptions"
                    :key="`${item.collateralEntity.description}-${item.collateralEntity.uniqueIdentifier}`"
                    :collateral-entity="item.collateralEntity"
                    :provided-collaterals="item.options"
                    :no-mapping-icon="isAllAutoMapped"
                    :is-no-match="isNoMatch(item.collateralEntity.evaluatedEntityId)"
                    :options-based-on-action="item.optionsBasedOnAction"
                    :submission-method="getSubmissionMethod(item.collateralEntity)"
                    :rbac-disabled="isDisabledByRbac"
                    @show-document="showDocument(item.collateralEntity)"
                    @update-collateral="updateCollateral($event, i)"
                    @update-no-match="$emit('update-no-match', $event)"
                />
            </Table>
        </RbacDisableGuard>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisableGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import { SubmissionMethod } from '@evidentid/tprm-portal-lib/models/common/SubmissionMethod.model';
    import { EntityRequirementDetails } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { CollateralEntity } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralEntity.model';
    import { CollateralMappingOptions } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralMappingOptions.model';
    import { ConciseCollateral } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/ConciseCollateral.model';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import CollateralMappingTableRow
        from '@/modules/entity-details/components/CollateralMappingTableRow/CollateralMappingTableRow.vue';
    import { CollateralMappingStatus } from '@/modules/entity-details/models/CollateralMappingStatus.model';
    import {
        getCollateralMappingOptionStatus,
    } from '@/modules/entity-details/utils/getCollateralMappingOptionStatus/getCollateralMappingOptionStatus';

    export default defineComponent({
        name: 'CollateralMappingTable',
        components: {
            Table,
            HeadCell,
            CollateralMappingTableRow,
            RbacDisableGuard,
        },
        props: {
            collateralEntities: {
                type: Array as PropType<CollateralEntity[]>,
                default: (): CollateralEntity[] => [] as CollateralEntity[],
            },
            collateralMappingOptions: {
                type: Array as PropType<CollateralMappingOptions[]>,
                default: (): CollateralMappingOptions[] => [] as CollateralMappingOptions[],
            },
            noMatchList: {
                type: Array as PropType<string[]>,
                default: (): string[] => [] as string[],
            },
            entityRequirementDetailsList: {
                type: Array as PropType<EntityRequirementDetails[]>,
                default: (): EntityRequirementDetails[] => [] as EntityRequirementDetails[],
            },
        },
        emits: [ 'show-document', 'update-collaterals', 'update-no-match' ],
        data() {
            return {
                RbacFeature,
            };
        },
        computed: {
            collateralAndOptions(): {
                collateralEntity: CollateralEntity;
                options: ConciseCollateral[];
                optionsBasedOnAction: boolean;
            }[] {
                return this.collateralEntities.map((collateralEntity) => {
                    const options = this.collateralMappingOptions
                        .find(
                            (option) => option.evaluatedEntityId === collateralEntity.evaluatedEntityId,
                        );

                    return {
                        collateralEntity,
                        options: options?.options || [],
                        optionsBasedOnAction: options?.optionsBasedOnAction || false,
                    };
                });
            },
            isAllAutoMapped(): boolean {
                return this.collateralAndOptions.every((item) =>
                    getCollateralMappingOptionStatus(
                        item.options, item.collateralEntity,
                    ) === CollateralMappingStatus.auto && !item.optionsBasedOnAction,
                );
            },
        },
        methods: {
            updateCollateral(collateralEntity: CollateralEntity, index: number): void {
                const modifiedEntities = [
                    ...this.collateralEntities.slice(0, index),
                    collateralEntity,
                    ...this.collateralEntities.slice(index + 1),
                ];
                this.$emit('update-collaterals', modifiedEntities);
            },
            isNoMatch(evaluatedEntityId: string): boolean {
                return this.noMatchList?.includes(evaluatedEntityId) || false;
            },
            getSubmissionMethod(collateral: CollateralEntity): SubmissionMethod | null {
                const details = this.entityRequirementDetailsList
                    .find((detail) => detail.coverageType === collateral.coverageType);
                return details?.coverage?.submissionMethod ?? null;
            },
            showDocument(collateral: CollateralEntity): void {
                const requirementDetails = this.entityRequirementDetailsList
                    .find((detail) => detail.coverageType === collateral.coverageType) ?? null;
                if (!requirementDetails) {
                    console.error('No collateral details found for collateral', collateral);
                    return;
                }
                this.$emit('show-document', requirementDetails);
            },
        },
    });
</script>
